import { AlertTriangle, HelpCircle, StickyNote } from 'lucide-react';
import { cn } from '../ui/utils';
import {
  ORGAN_LABEL,
  CONFIDENCE_LABEL,
  bandStyle,
  isRedFlagCapped,
  isInsufficient,
  type OrganScore,
  unscorableGap,
  CONFIDENCE_EMPHASIS,
} from './organHealth';
import { BandChip } from './BandChip';
import { PriorityChip } from './PriorityChip';
import { RiskMeter } from './RiskTrack';
import { OrganTrendSparkline } from './OrganTrendSparkline';
import type { OrganScoreHistoryPoint } from '../../api/organHealthApi';

/**
 * One organ summary card: label, score/100, band chip (icon + glyph + text),
 * priority chip (v0.12 — the composite's management posture, outlined so it
 * can never be mistaken for the filled severity chip),
 * and a confidence line. Renders the insufficient-data and red-flag-capped
 * states. Display-only. Optionally clickable to open the detail view.
 *
 * The score itself is never coloured by its band — it stays `text-primary`.
 * The chip carries the band; colouring a large number by severity would be
 * gauge theatre and implies more precision than the method claims.
 */
export function OrganScoreCard({
  organ,
  selected,
  onSelect,
  history,
}: {
  organ: OrganScore;
  selected?: boolean;
  onSelect?: (organ: OrganScore) => void;
  /** Score history for the sparkline; omitted/empty renders the neutral placeholder. */
  history?: OrganScoreHistoryPoint[];
}) {
  const style = bandStyle(organ.band);
  // unscorableGap, not the bare reason label: the generic insufficient_data
  // code renders "Not enough data to score", while to_improve[0] carries the
  // actionable equivalent the scorer already computed ("Add LVEF
  // (echocardiography) to score this organ."). The drawer row uses the same
  // helper, so card and drawer can never name the gap differently.
  // The one test that would unlock this score -- prefers the scorer's
  // specific reason, falls back to its first to_improve line. Rendered as a
  // call-to-action, not a passive gap: an unscored card should read as an
  // order-entry prompt, the same seam the drawer rows already use.
  const gap = unscorableGap(organ);
  const insufficient = isInsufficient(organ);
  const capped = isRedFlagCapped(organ);
  const interactive = typeof onSelect === 'function';

  return (
    <button
      type="button"
      disabled={!interactive}
      onClick={interactive ? () => onSelect?.(organ) : undefined}
      aria-pressed={selected}
      className={cn(
        'group flex w-full flex-col gap-3 rounded border bg-surface-raised p-4 text-left transition-all duration-150',
        capped ? 'border-l-[3px] rounded-l-none' : '',
        style.surface,
        interactive && 'hover:shadow-sm cursor-pointer',
        selected && 'ring-2 ring-ring ring-offset-1 ring-offset-background',
      )}
      style={capped ? { borderLeftColor: 'var(--capped-rule)', backgroundImage: style.hatch } : undefined}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-[0.08em] text-text-tertiary">
          {ORGAN_LABEL[organ.organ]}
        </span>
        {/* An unscored card keeps its state chip here — there is no headline
            pair to join, and the dashed "no data" chip is the whole message. */}
        {insufficient ? <BandChip organ={organ} /> : null}
      </div>

      {/* An unscored organ must not compete visually with a scored one. The
          em-dash previously rendered at the same 3xl weight as a real number,
          so a card saying "we could not look" drew the eye as hard as one
          saying "81". Absent data is de-emphasised and, where the scorer said
          WHY, the card names the missing test instead of showing a placeholder
          glyph — a gap a clinician can close beats a dash they must decode. */}
      {insufficient ? (
        <div className="flex flex-col gap-1">
          <span className="text-xl font-medium leading-none text-text-disabled">—</span>
          {gap ? (
            <span className="inline-flex items-start gap-1 text-[11px] leading-snug font-medium text-text-secondary">
              <span aria-hidden>→</span>
              <span>{gap}</span>
            </span>
          ) : (
            <span className="text-[11px] text-text-tertiary">Not scored</span>
          )}
        </div>
      ) : (
        /* The HEADLINE group: the number, then the two chips that interpret it.
           Tighter than the card's own gap on purpose — with uniform spacing the
           chips sat equidistant from the score above and the trend below, so
           they read as independent facts rather than as statements about the
           number they belong to.

           BAND AND PRIORITY SIT SIDE BY SIDE, at `md` (clinician ruling
           2026-07-29: "Band vs priority must be visually prominent"). They were
           previously split — band in the header, priority under the score — so
           the one contrast that keeps them from being confused, FILLED severity
           against OUTLINED posture, was never actually shown on screen. They
           answer two different questions (how damaged the organ is, versus how
           hard to work the problem) and a clinician can only tell them apart by
           seeing them together.

           The score steps down from 3xl to 2xl in the same move. It stays the
           anchor, but the rubric's own standing note is that these numbers are
           an uncalibrated expert prior — so the least validated element on the
           card should not also be the loudest. */
        <div className="flex flex-col items-start gap-2">
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-medium tabular-nums leading-none text-text-primary">
              {organ.score}
            </span>
            <span className="text-sm font-medium text-text-tertiary">/100</span>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <BandChip organ={organ} size="md" />
            <PriorityChip organ={organ} size="md" />
          </div>
        </div>
      )}

      {!insufficient && history !== undefined && (
        <OrganTrendSparkline points={history} band={organ.band} />
      )}

      {/* The second track. Shown whenever it was scored, including on an organ
        * whose function could not be graded — "we can't stage this kidney but
        * her modifiable risk is critical" is the actionable half of the picture,
        * and hiding it behind the function score would lose it. */}
      {typeof organ.risk_score === 'number' && (
        <RiskMeter score={organ.risk_score} band={organ.risk_band} />
      )}

      <div className="flex items-center justify-between border-t border-border-subtle pt-2 text-xs">
        {/* Confidence is not decoration. A low-confidence 92 and a
            high-confidence 92 are the same number meaning different things, so
            low is marked (icon + warning tone) while high stays quiet. Never
            colour alone — the word is always present.

            But ONLY where a score exists to be confident about. The scorer
            reports "low" for every unscorable organ (trivially true: nothing
            was measured), and stamping that on unscored cards put "Low
            confidence" on 10 of 10 cards for a typical sparse patient —
            identical labels carry no signal, and the two that MATTERED (a
            scored kidney resting on eGFR alone; a scored pancreas missing a
            primary) drowned in eight that meant nothing. An unscored card
            already says "Function not scored" and names the unlock test;
            adding "low confidence" to that is noise about an absence. */}
        {insufficient && typeof organ.risk_score !== 'number' ? null : (
          <span className={cn('inline-flex items-center gap-1', CONFIDENCE_EMPHASIS[organ.confidence].className)}>
            {CONFIDENCE_EMPHASIS[organ.confidence].marked && (
              <HelpCircle className="size-3 shrink-0" aria-hidden="true" />
            )}
            {CONFIDENCE_LABEL[organ.confidence]}
          </span>
        )}
        {/* The prose note exists behind a click, and nothing said so — the
            audit's discoverability item. A quiet marker, not a teaser: it only
            renders when a stored summary is actually attached. */}
        {organ.summary?.text ? (
          <span
            className="inline-flex items-center gap-1 text-text-tertiary"
            title="This organ has an AI-audited note — open the card to read it."
          >
            <StickyNote className="size-3" aria-hidden="true" />
            note
          </span>
        ) : null}
        {capped && (
          <span className="inline-flex items-center gap-1 font-medium text-band-critical-text">
            <AlertTriangle className="size-3" aria-hidden="true" />
            Capped
          </span>
        )}
      </div>
    </button>
  );
}
